/* MATOSHI — scroll reveals, hero choreography, parallax.
   GSAP is optional: every effect has a CSS/IO fallback. */
(function () {
  'use strict';
  var reduced = matchMedia('(prefers-reduced-motion: reduce)').matches
    || document.documentElement.dataset.motion === 'reduced';

  /* ---------- reveal on scroll ---------- */
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      var el = e.target;
      var delay = parseInt(el.dataset.revealDelay || '0', 10);
      setTimeout(function () { el.classList.add('is-in'); }, reduced ? 0 : delay);
      io.unobserve(el);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

  function bindReveals(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('[data-reveal]:not(.is-in)'), function (el) {
      io.observe(el);
    });
    // stagger children of [data-reveal-group]; the cascade is capped so long
    // listings never keep the last item waiting behind a queue of delays
    Array.prototype.forEach.call((root || document).querySelectorAll('[data-reveal-group]'), function (g) {
      var n = g.children.length;
      var step = n > 8 ? Math.max(24, Math.round(420 / n)) : 60;
      Array.prototype.forEach.call(g.children, function (c, i) {
        if (!c.hasAttribute('data-reveal')) { c.setAttribute('data-reveal', ''); }
        c.dataset.revealDelay = String(Math.min(420, i * step));
        io.observe(c);
      });
    });
  }
  bindReveals();
  window.MatoshiReveal = bindReveals;

  /* ---------- animated counters ---------- */
  var cio = new IntersectionObserver(function (es) {
    es.forEach(function (e) {
      if (!e.isIntersecting) return;
      var el = e.target, to = parseFloat(el.dataset.count), dur = 1100, t0 = performance.now();
      var suffix = el.dataset.countSuffix || '';
      if (reduced) { el.textContent = to.toLocaleString('de-DE') + suffix; cio.unobserve(el); return; }
      (function step(t) {
        var p = Math.min(1, (t - t0) / dur), eased = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(to * eased).toLocaleString('de-DE') + suffix;
        if (p < 1) requestAnimationFrame(step);
      })(t0);
      cio.unobserve(el);
    });
  }, { threshold: 0.4 });
  Array.prototype.forEach.call(document.querySelectorAll('[data-count]'), function (el) { cio.observe(el); });

  /* ---------- hero ---------- */
  var hero = document.querySelector('[data-hero]');
  if (hero) {
    // Entrance: CSS handles it via .is-ready + per-element delays.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { hero.classList.add('is-ready'); });
    });

    var stack = hero.querySelector('hero-stack');
    if (stack && !reduced) {
      /* pointer tilt */
      var rx = 0, ry = 0, tx = 0, ty = 0, raf = null;
      function loop() {
        tx += (rx - tx) * 0.08; ty += (ry - ty) * 0.08;
        stack.style.setProperty('--tilt-x', tx.toFixed(2) + 'deg');
        stack.style.setProperty('--tilt-y', ty.toFixed(2) + 'deg');
        if (Math.abs(rx - tx) > 0.01 || Math.abs(ry - ty) > 0.01) raf = requestAnimationFrame(loop);
        else raf = null;
      }
      hero.addEventListener('pointermove', function (e) {
        if (matchMedia('(hover: none)').matches) return;
        var r = hero.getBoundingClientRect();
        ry = ((e.clientX - r.left) / r.width - 0.5) * 16;
        rx = -((e.clientY - r.top) / r.height - 0.5) * 12;
        if (!raf) raf = requestAnimationFrame(loop);
      }, { passive: true });
      hero.addEventListener('pointerleave', function () {
        rx = 0; ry = 0; if (!raf) raf = requestAnimationFrame(loop);
      });
    }

    /* scroll response: fade + scale text, drift cards */
    if (!reduced) {
      var txt = hero.querySelector('[data-hero-text]');
      var cards = Array.prototype.slice.call(hero.querySelectorAll('[data-hero-card]'));
      var ticking = false;
      function onScroll() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          var y = window.scrollY, h = window.innerHeight * 0.6;
          var p = Math.min(1, y / h);
          if (txt) { txt.style.opacity = String(1 - p * 0.45); txt.style.transform = 'scale(' + (1 - p * 0.04) + ')'; }
          cards.forEach(function (c, i) { c.style.setProperty('--drift', (-p * (26 + i * 14)).toFixed(1) + 'px'); });
          hero.style.setProperty('--aura-shift', (p * 30).toFixed(1) + 'px');
          ticking = false;
        });
      }
      addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }

  /* ---------- hero card auto-cycle ---------- */
  var stackEl = document.querySelector('hero-stack');
  if (stackEl) {
    customElements.define('hero-stack', class extends HTMLElement {
      connectedCallback() {
        this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-hero-card]'));
        if (this.cards.length < 2) return;
        this.i = 0;
        this.paint();
        this.setAttribute('tabindex', '0');
        this.setAttribute('role', 'group');
        this.setAttribute('aria-label', 'Ausgewählte Produkte, mit Pfeiltasten wechseln');
        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); this.cycle(1); }
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); this.cycle(-1); }
        });
        var host = this.closest('.hero__visual') || this;
        var prev = host.querySelector('[data-stack-prev]');
        var next = host.querySelector('[data-stack-next]');
        if (prev) prev.addEventListener('click', () => { this.cycle(-1); this.restart(); });
        if (next) next.addEventListener('click', () => { this.cycle(1); this.restart(); });

        if (reduced) return;
        this.interval = 5000;
        this.start = () => { clearInterval(this.t); this.t = setInterval(() => this.cycle(1), this.interval); };
        this.stop = () => clearInterval(this.t);
        this.restart = () => { this.stop(); this.start(); };
        // Hovering speeds the rotation up rather than freezing it — the stack
        // should feel alive exactly while someone is looking at it.
        host.addEventListener('pointerenter', () => { this.interval = 2200; this.restart(); });
        host.addEventListener('pointerleave', () => { this.interval = 5000; this.restart(); });
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => this.start());
        document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));
        this.start();
      }
      disconnectedCallback() { clearInterval(this.t); }
      restart() { /* replaced when motion is enabled */ }
      cycle(d) { this.i = (this.i + d + this.cards.length) % this.cards.length; this.paint(); }
      paint() {
        var n = this.cards.length;
        var label = document.querySelector('[data-stack-label]');
        if (label) label.textContent = (this.i + 1) + ' von ' + n + ' im Drop';
        this.cards.forEach((c, idx) => {
          var pos = (idx - this.i + n) % n;
          c.dataset.pos = pos;
          c.style.zIndex = String(n - pos);
          c.setAttribute('aria-hidden', pos === 0 ? 'false' : 'true');
          Array.prototype.forEach.call(c.querySelectorAll('a,button'), function (el) {
            el.tabIndex = pos === 0 ? 0 : -1;
          });
        });
      }
    });
  }

  /* ---------- marquee duration from content width (seamless) ---------- */
  Array.prototype.forEach.call(document.querySelectorAll('.marquee'), function (m) {
    var track = m.querySelector('.marquee__track');
    if (!track) return;
    var speed = parseFloat(m.dataset.speed || '60'); // px per second
    var w = track.scrollWidth;
    if (w > 0) m.style.setProperty('--marquee-dur', (w / speed).toFixed(1) + 's');
  });

  /* ---------- View Transitions between pages ---------- */
  if (!reduced && 'startViewTransition' in document) {
    document.documentElement.style.setProperty('view-transition-name', 'root');
  }
})();
